import { useSyncExternalStore } from 'react';

import { getCachedEditor } from '@/hooks/use-block-editor/editor-cache';
import { isEditorAlive } from '@/utils/editor-utils';
import { type AgentEditScope, type AgentEditTarget, createDocumentEditTarget, getAgentEditScope } from '../edit-target';
import {
  type AgentRunActivityState,
  type AgentRunEvent,
  type AgentRunIdentity,
  type AgentRunState,
  type AgentRunTerminalizationSource,
  type AgentRunUiState,
  createAgentRunState,
  DEFAULT_AGENT_RUN_UI_STATE,
  deriveEditorState,
  type DurableEdit,
  getAgentRunUiSnapshot as getRawAgentRunUiSnapshot,
  type EditorState,
  type PendingReviewCommit,
  type RevertData,
  isAgentRunAwaitingPostEditSummary,
  isAgentRunLoading,
  reduceAgentRunEvent,
} from './agent-run-model';
import { registerAutoScroll } from '../streaming/auto-scroll';
import {
  clearAllReplaceSelectionSessions,
  clearReplaceSelectionSessionsForRequest,
} from '../streaming/replace-selection-sessions';
import { recordGenerationStart } from '../note-generation-session-store';
import { TIPTAP_TOOL_NAMES } from '@flintmint/web-shared/utils/llm-message-utils';
import { EDITOR_WRITE_TOOL_NAMES } from '@flintmint/core-shared/utils/editor-write-tool';

export type {
  AgentRunActivityState,
  AgentRunEvent,
  AgentRunState,
  AgentRunTerminalizationSource,
  AgentRunUiState,
  EditorState,
  RevertData,
};
export { isAgentRunAwaitingPostEditSummary, isAgentRunLoading };

type CachedEditorKeepAliveContext = {
  requestLoading: boolean;
  runRequestId?: string;
  agentActive: boolean;
};

const agentRunStore = new Map<string, AgentRunState>();
const agentRunListeners = new Set<() => void>();
const agentRunEventListeners = new Set<(event: AgentRunStoreEvent) => void>();
// Memoized per record identity: the reducer replaces the record exactly when a UI-relevant event
// applies, so one projection per record gives useSyncExternalStore a stable reference otherwise.
const agentRunUiSnapshotMemo = new WeakMap<AgentRunState, AgentRunUiState>();
const agentRunStateClearers = new Set<() => void>();

// Lets sibling per-run side state (e.g. the streaming preview engine) hook into
// clear-all without the store importing it, which would create an import cycle.
export function registerAgentRunStateClearer(clear: () => void): void {
  agentRunStateClearers.add(clear);
}

export type AgentRunStoreEvent = {
  requestId: string;
  runId: number;
  event: AgentRunEvent;
};

function emitAgentRunStoreChange(): void {
  agentRunListeners.forEach((listener) => listener());
}

function emitAgentRunEvent(event: AgentRunStoreEvent): void {
  agentRunEventListeners.forEach((listener) => {
    try {
      listener(event);
    } catch (error) {
      console.error('Agent run event listener failed:', error);
    }
  });
}

function getOrCreateAgentRun(requestId: string): AgentRunState {
  if (!agentRunStore.has(requestId)) {
    agentRunStore.set(requestId, createAgentRunState());
  }
  return agentRunStore.get(requestId)!;
}

export function getAgentRunState(requestId: string): AgentRunState | undefined {
  return agentRunStore.get(requestId);
}

export function isCurrentAgentRunSnapshot(requestId: string, runId: number, identity?: AgentRunIdentity): boolean {
  if (!identity) {
    return false;
  }

  const currentRun = agentRunStore.get(requestId);
  return !!currentRun && currentRun.identity === identity && currentRun.currentRunId === runId;
}

function getAgentRunUiSnapshot(requestId?: string): AgentRunUiState {
  if (!requestId) return DEFAULT_AGENT_RUN_UI_STATE;

  const run = agentRunStore.get(requestId);
  if (!run) return DEFAULT_AGENT_RUN_UI_STATE;

  const cached = agentRunUiSnapshotMemo.get(run);
  if (cached) return cached;

  const snapshot = getRawAgentRunUiSnapshot(run);
  agentRunUiSnapshotMemo.set(run, snapshot);
  return snapshot;
}

export const resumeAgentRunAutoScroll = (requestId: string): void => {
  agentRunStore.get(requestId)?.scrollRegistration?.reengage();
};

export const clearRunScrollCleanup = (requestId: string) => {
  const run = agentRunStore.get(requestId);
  run?.scrollRegistration?.cleanup();
  if (run) {
    run.scrollRegistration = null;
  }
};

export function setAgentRunPendingReviewCommit(requestId: string, commit: PendingReviewCommit): void {
  getOrCreateAgentRun(requestId).pendingReviewCommit = commit;
}

export function getAgentRunPendingReviewCommit(requestId: string): PendingReviewCommit | null {
  return agentRunStore.get(requestId)?.pendingReviewCommit ?? null;
}

export function clearAgentRunPendingReviewCommit(requestId: string, commit: PendingReviewCommit): void {
  const run = agentRunStore.get(requestId);
  if (run?.pendingReviewCommit === commit) {
    run.pendingReviewCommit = null;
  }
}

export function clearAllAgentRunState(): void {
  agentRunStateClearers.forEach((clear) => clear());
  clearAllReplaceSelectionSessions();
  agentRunStore.forEach((run) => run.scrollRegistration?.cleanup());
  agentRunStore.clear();
  emitAgentRunStoreChange();
}

export function startAgentRun(requestId: string): number {
  clearRunScrollCleanup(requestId);
  clearReplaceSelectionSessionsForRequest(requestId);
  recordGenerationStart(requestId);
  const nextRunId = getOrCreateAgentRun(requestId).currentRunId + 1;

  agentRunStore.set(requestId, createAgentRunState(nextRunId));
  emitAgentRunStoreChange();

  return nextRunId;
}

export function getCurrentAgentRunId(requestId: string): number {
  return agentRunStore.get(requestId)?.currentRunId ?? 0;
}

export function publishAgentRunEvent(requestId: string, runId: number, event: AgentRunEvent): void {
  const session = getOrCreateAgentRun(requestId);
  if (session.currentRunId !== runId) {
    return;
  }

  const nextSession = reduceAgentRunEvent(session, event);
  if (!nextSession) return;

  agentRunStore.set(requestId, nextSession);

  // Auto-scroll follows the AI caret during tiptapEdit streaming.
  // Start on agent_write_started; stop on any terminal event.
  // Terminal cleanup lives here (not only in the lifecycle dispatcher) because
  // some paths publish terminal events directly — e.g. agent-tool-call-handlers
  // publishes 'errored' on missing editor without going through the dispatcher.
  // Scroll registration is a store-owned side field, so it mutates onto the current (new) record.
  if (event.type === 'agent_write_started' && nextSession.streamingEnabled && !nextSession.scrollRegistration) {
    const editor = getCachedEditor(requestId)?.editor;
    if (isEditorAlive(editor)) {
      const registration = registerAutoScroll(editor);
      if (registration) {
        nextSession.scrollRegistration = registration;
      }
    }
  } else if (['completed', 'cancelled', 'errored'].includes(event.type)) {
    clearRunScrollCleanup(requestId);
    clearReplaceSelectionSessionsForRequest(requestId);
  }

  emitAgentRunEvent({ requestId, runId, event });
  emitAgentRunStoreChange();
}

export function subscribeAgentRunEvents(listener: (event: AgentRunStoreEvent) => void): () => void {
  agentRunEventListeners.add(listener);
  return () => agentRunEventListeners.delete(listener);
}

export function useAgentRunUiState(requestId?: string): AgentRunUiState {
  return useSyncExternalStore(
    (listener) => {
      agentRunListeners.add(listener);
      return () => agentRunListeners.delete(listener);
    },
    () => getAgentRunUiSnapshot(requestId),
    () => DEFAULT_AGENT_RUN_UI_STATE
  );
}

export function useEditorState(requestId?: string): EditorState {
  return useAgentRunUiState(requestId).editorState;
}

export function tryClaimAgentRunTerminalization(
  requestId: string,
  runId: number,
  source: AgentRunTerminalizationSource
): boolean {
  const session = getOrCreateAgentRun(requestId);
  if (session.currentRunId !== runId || !isAgentRunLoading(session) || session.terminalizationSource) return false;
  session.terminalizationSource = source;
  return true;
}

export function releaseAgentRunTerminalizationClaim(
  requestId: string,
  runId: number,
  source: AgentRunTerminalizationSource
): boolean {
  const session = agentRunStore.get(requestId);
  if (!session || session.currentRunId !== runId || session.terminalizationSource !== source) {
    return false;
  }

  session.terminalizationSource = null;
  return true;
}

export function hasAgentRunClaimedTerminalization(requestId: string): boolean {
  return getOrCreateAgentRun(requestId).terminalizationSource != null;
}

export function shouldKeepCachedEditorAlive({
  requestLoading,
  runRequestId,
  agentActive,
}: CachedEditorKeepAliveContext): boolean {
  return requestLoading || agentActive || isAgentRunLoading(runRequestId ? agentRunStore.get(runRequestId) : undefined);
}

function shouldIgnorePostTargetedWriteTools(run?: AgentRunState, toolName?: string): boolean {
  if (!run || getAgentEditScope(run.editTarget) !== 'targeted' || !isAgentRunAwaitingPostEditSummary(run)) {
    return false;
  }

  if (toolName == null) {
    return true;
  }

  return EDITOR_WRITE_TOOL_NAMES.has(toolName) || TIPTAP_TOOL_NAMES.has(toolName);
}

export function shouldIgnoreAgentWriteStart(requestId: string): boolean {
  const run = agentRunStore.get(requestId);
  return (
    !!run && (run.terminalizationSource != null || deriveEditorState(run) === 'review' || shouldIgnorePostTargetedWriteTools(run))
  );
}

export function getToolExecutionIgnoreReason(requestId: string, toolName: string): string | null {
  const run = agentRunStore.get(requestId);
  if (!run) {
    return null;
  }

  if (run.terminalizationSource != null) {
    return 'Run already terminalized';
  }

  if (EDITOR_WRITE_TOOL_NAMES.has(toolName) && deriveEditorState(run) === 'review') {
    return 'Persisted review already committed';
  }

  if (shouldIgnorePostTargetedWriteTools(run, toolName)) {
    return 'Targeted edit already applied; return a brief confirmation instead of using more tools';
  }

  return null;
}

export function clearAgentRunEditSnapshot(requestId: string, runId: number): void {
  const run = agentRunStore.get(requestId);
  if (!run || run.currentRunId !== runId) {
    return;
  }

  run.revertData = null;
  run.startedFromEmptyEditor = false;
}

export function setAgentRunDurableEdit(requestId: string, runId: number, durableEdit: DurableEdit | null): void {
  const run = agentRunStore.get(requestId);
  if (run?.currentRunId === runId) {
    run.durableEdit = durableEdit;
  }
}

export function getAgentRunDurableEdit(requestId: string): DurableEdit | null {
  return agentRunStore.get(requestId)?.durableEdit ?? null;
}

export function setAgentRunRevertData(requestId: string, data: RevertData | null): void {
  getOrCreateAgentRun(requestId).revertData = data;
}

export function setAgentRunEditTarget(requestId: string, editTarget: AgentEditTarget): void {
  getOrCreateAgentRun(requestId).editTarget = editTarget;
}

export function getAgentRunEditScope(requestId: string): AgentEditScope {
  return getAgentEditScope(getAgentRunEditTarget(requestId));
}

export function getAgentRunEditTarget(requestId: string): AgentEditTarget {
  return agentRunStore.get(requestId)?.editTarget ?? createDocumentEditTarget();
}

export function getAgentRunRevertData(requestId: string): RevertData | null {
  return agentRunStore.get(requestId)?.revertData ?? null;
}

export function setAgentRunStartedFromEmptyEditor(requestId: string, value: boolean): void {
  getOrCreateAgentRun(requestId).startedFromEmptyEditor = value;
}

export function getAgentRunStartedFromEmptyEditor(requestId: string): boolean {
  return agentRunStore.get(requestId)?.startedFromEmptyEditor ?? false;
}

export function setAgentRunStreamingEnabled(requestId: string, enabled: boolean): void {
  getOrCreateAgentRun(requestId).streamingEnabled = enabled;
}

export function getAgentRunStreamingEnabled(requestId: string): boolean {
  return agentRunStore.get(requestId)?.streamingEnabled ?? true;
}
